import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Divider, 
  Snackbar,
  Alert
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

const ContactPage: React.FC = () => {
  useEffect(() => {
    // Update the document title
    document.title = 'İletişim - Akademi Kadro';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Akademi Kadro ile iletişime geçin. Öneri, şikayet ve sorularınız için bize ulaşın.');
    }
    
    // Clean up when component unmounts
    return () => {
      document.title = 'Akademi Kadro - Güncel Haberler';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Akademi Kadro - Güncel haberler, son dakika gelişmeleri, ekonomi, siyaset, spor, eğitim ve kültür haberleri');
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    subject: false,
    message: false
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {
      name: formData.name.trim() === '',
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
      subject: formData.subject.trim() === '',
      message: formData.message.trim() === ''
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Normally we would send to an API here
      console.log('Form submitted:', formData);
      
      // For demonstration, simulate successful submission
      setSubmitSuccess(true);
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } else {
      setSubmitError(true);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSubmitSuccess(false);
    setSubmitError(false);
  };
  
  return (
    <Box sx={{ py: { xs: 3, md: 5 } }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4, md: 6 }, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          İletişim
        </Typography>
        <Divider sx={{ mb: { xs: 3, md: 5 } }} />
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 6 } }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Bize Ulaşın
            </Typography>
            <Typography variant="body1" paragraph>
              Aşağıdaki formu doldurarak bize mesaj gönderebilirsiniz. En kısa sürede size geri dönüş yapacağız.
            </Typography>
            
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Adınız Soyadınız"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                helperText={errors.name ? 'Adınızı girmelisiniz' : ''}
                required
                sx={{ mb: { xs: 2, md: 2.5 } }}
                inputProps={{ style: { fontSize: '1rem' } }}
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="E-posta Adresiniz"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                helperText={errors.email ? 'Geçerli bir e-posta adresi girmelisiniz' : ''}
                required
                sx={{ mb: { xs: 2, md: 2.5 } }}
                inputProps={{ style: { fontSize: '1rem' } }}
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Konu"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                error={errors.subject}
                helperText={errors.subject ? 'Konu girmelisiniz' : ''}
                required
                sx={{ mb: { xs: 2, md: 2.5 } }}
                inputProps={{ style: { fontSize: '1rem' } }}
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Mesajınız"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                error={errors.message}
                helperText={errors.message ? 'Mesaj girmelisiniz' : ''}
                required
                sx={{ mb: { xs: 2, md: 2.5 } }}
                inputProps={{ style: { fontSize: '1rem' } }}
              />
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ 
                  mt: 1,
                  py: { xs: 1.5, md: 1 },
                  px: { xs: 3, md: 2 },
                  fontSize: { xs: '1rem', md: '0.9rem' }
                }}
              >
                Gönder
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ mt: { xs: 2, md: 0 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              İletişim Bilgilerimiz
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', mb: 3, alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ mr: 2, color: 'primary.main', fontSize: { xs: 28, md: 24 }, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Adres
                  </Typography>
                  <Typography variant="body1">
                    Atatürk Bulvarı No:123<br />
                    Çankaya/Ankara
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', mb: 3, alignItems: 'flex-start' }}>
                <PhoneIcon sx={{ mr: 2, color: 'primary.main', fontSize: { xs: 28, md: 24 }, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Telefon
                  </Typography>
                  <Typography variant="body1">
                    +90 312 123 45 67
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', mb: 3, alignItems: 'flex-start' }}>
                <EmailIcon sx={{ mr: 2, color: 'primary.main', fontSize: { xs: 28, md: 24 }, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    E-posta
                  </Typography>
                  <Typography variant="body1">
                    akademikadro@gmail.com
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ my: 4 }} />
            
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Çalışma Saatleri
            </Typography>
            <Typography variant="body1" paragraph>
              Hafta içi: 09:00 - 18:00<br />
              Hafta sonu: Kapalı
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              * Acil haberler ve önemli konular için 7/24 destek ekibimize ulaşabilirsiniz.
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      <Snackbar open={submitSuccess} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Mesajınız başarıyla gönderildi. En kısa sürede size geri dönüş yapacağız.
        </Alert>
      </Snackbar>
      
      <Snackbar open={submitError} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          Form gönderilirken bir hata oluştu. Lütfen tüm alanları kontrol edip tekrar deneyiniz.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage; 